import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import { formatYYYYMMDD, getStartOfWeek, parseDDMMYYYY } from './dateService';
import { getOrderDeliveryDate, normalizeOrderDate } from '../utils/orderDateUtils';
import { getQueueOrderKey } from './printQueueModel';
import {
  isLondonSmokeReturnAddressPhrase,
  scrubReturnAddressFromLabelText,
} from '../utils/shippingLabelReturnAddressIgnore';

if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
}

/** Soft boost so labels for the packing week print/match together (not a hard filter). */
export const WEEK_PROXIMITY_BOOST = Object.freeze({
  active: 40,
  adjacent: 18,
  outside: 0,
  unknown: 0,
});

/**
 * OrderDesk week folders are named as the last day of the week (usually Friday).
 * Bounds are Monday → Friday for the week containing `isoYmd`.
 * @returns {{ start: string, end: string, folderEnd: string }|null}
 */
export function getOrderDeskWeekBounds(isoYmd) {
  const mondayIso = getStartOfWeek(isoYmd);
  if (!mondayIso) return null;
  const monday = new Date(`${mondayIso}T12:00:00`);
  if (Number.isNaN(monday.getTime())) return null;
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const folderEnd = formatYYYYMMDD(friday);
  return { start: mondayIso, end: folderEnd, folderEnd };
}

export function shiftOrderDeskWeekBounds(bounds, weekDelta) {
  if (!bounds?.start || !bounds?.end) return null;
  const shiftDays = Number(weekDelta || 0) * 7;
  const shiftIso = (iso) => {
    const date = new Date(`${iso}T12:00:00`);
    date.setDate(date.getDate() + shiftDays);
    return formatYYYYMMDD(date);
  };
  return {
    start: shiftIso(bounds.start),
    end: shiftIso(bounds.end),
    folderEnd: shiftIso(bounds.folderEnd || bounds.end),
  };
}

const isoInInclusiveRange = (iso, start, end) =>
  Boolean(iso && start && end && iso >= start && iso <= end);

/**
 * Classify an order relative to the Printing Toolkit delivery-date week.
 * @returns {'active'|'adjacent'|'outside'|'unknown'}
 */
export function classifyOrderWeekProximity(order, workingDeliveryDate) {
  const workingIso = normalizeOrderDate(String(workingDeliveryDate || ''));
  if (!workingIso) return 'unknown';

  const active = getOrderDeskWeekBounds(workingIso);
  if (!active) return 'unknown';
  const previous = shiftOrderDeskWeekBounds(active, -1);
  const next = shiftOrderDeskWeekBounds(active, 1);

  const deliveryIso = normalizeOrderDate(String(getOrderDeliveryDate(order) || ''));
  if (deliveryIso) {
    if (isoInInclusiveRange(deliveryIso, active.start, active.end)) return 'active';
    if (
      (previous && isoInInclusiveRange(deliveryIso, previous.start, previous.end)) ||
      (next && isoInInclusiveRange(deliveryIso, next.start, next.end))
    ) {
      return 'adjacent';
    }
    return 'outside';
  }

  const folderRaw = String(order?.folderName || order?.folder_name || '').trim();
  const folderIso =
    normalizeOrderDate(folderRaw) ||
    (() => {
      const parsed = parseDDMMYYYY(folderRaw);
      return parsed ? formatYYYYMMDD(parsed) : null;
    })();
  if (folderIso) {
    if (folderIso === active.folderEnd) return 'active';
    if (folderIso === previous?.folderEnd || folderIso === next?.folderEnd) {
      return 'adjacent';
    }
    return 'outside';
  }

  return 'unknown';
}

const normalize = (value) =>
  String(value || '')
    .replace(/^#/, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

const normalizeWords = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const uniqueValues = (values, minimumLength = 2) =>
  Array.from(
    new Set(
      values
        .map(normalizeWords)
        .filter((value) => value.length >= minimumLength),
    ),
  );

/** Split values like `3656 / #11713` into separate match tokens. */
export function splitOrderIdentifierTokens(raw) {
  return String(raw || '')
    .split(/[/|,;]+|\band\b/i)
    .flatMap((part) => String(part).trim().split(/\s+/))
    .map((part) => part.trim())
    .filter(Boolean);
}

const CHECKOUT_ID_KEY_RE =
  /customer\s*ref|customer\s*reference|purchase\s*order|^po$|order\s*number|source\s*id/i;

function collectCheckoutIdentifierValues(order) {
  const checkout = order?.checkout_data || order?.checkoutData;
  if (!checkout || typeof checkout !== 'object' || Array.isArray(checkout)) {
    return [];
  }
  return Object.entries(checkout)
    .filter(([key, value]) => CHECKOUT_ID_KEY_RE.test(String(key || '')) && value != null && value !== '')
    .map(([, value]) => value);
}

/**
 * Identifiers used when matching packing-slip / carrier page text to an order.
 * Includes source ID, OrderDesk / internal order numbers, purchase orders, and
 * checkout Customer Ref values (which may hold either ID, or both joined by /).
 */
export function orderIdentifiers(order) {
  const rawValues = [
    getQueueOrderKey(order),
    order?.source_id,
    order?.sourceId,
    order?.orderId,
    order?.order_id,
    order?.order_number,
    order?.orderNumber,
    order?.id,
    order?.purchaseOrder,
    order?.purchase_order,
    ...collectCheckoutIdentifierValues(order),
  ];
  const tokens = rawValues.flatMap((value) => splitOrderIdentifierTokens(value));
  return Array.from(
    new Set(
      tokens
        .map(normalize)
        .filter((value) => value.length >= 3),
    ),
  );
}

const orderMatchProfile = (order) => {
  const shipping = order?.shipping || order?.shippingAddress || order?.shipping_address || {};
  const customer = order?.customer || order?.billing || order?.billingAddress || {};
  const shippingFirst = shipping.first_name || shipping.firstName || order?.shippingFirstName;
  const shippingLast = shipping.last_name || shipping.lastName || order?.shippingLastName;
  const customerFirst = customer.first_name || customer.firstName || order?.customerFirstName;
  const customerLast = customer.last_name || customer.lastName || order?.customerLastName;
  const shippingMethod = String(
    order?.shipping_method || order?.shippingMethod || order?.Method || '',
  ).trim();
  return {
    identifiers: orderIdentifiers(order),
    shippingMethod,
    isTracked24Order: isTracked24ShippingMethod(shippingMethod),
    names: uniqueValues([
      `${shippingFirst || ''} ${shippingLast || ''}`,
      `${customerFirst || ''} ${customerLast || ''}`,
      shipping.name,
      customer.name,
      order?.customerName,
      order?.shippingName,
    ], 4),
    companies: uniqueValues([
      shipping.company,
      customer.company,
      order?.customerCompany,
      order?.companyName,
      order?.company,
    ], 4),
    addressLines: uniqueValues([
      shipping.address1,
      shipping.address_1,
      shipping.line1,
      order?.shippingStreet,
      customer.address1,
      customer.address_1,
      customer.line1,
    ], 5),
    cities: uniqueValues([
      shipping.city,
      order?.shippingCity,
      customer.city,
    ], 3),
    postcodes: Array.from(
      new Set(
        [
          shipping.postal_code,
          shipping.postalCode,
          shipping.postCode,
          shipping.zip,
          order?.shippingPostalCode,
          customer.postal_code,
          customer.postalCode,
          customer.postCode,
          customer.zip,
        ]
          .map(normalize)
          .filter((value) => value.length >= 4),
      ),
    ),
  };
};

/** Royal Mail Tracked 24 label page (no order number / Customer Ref on the face). */
export function isTracked24LabelPage(text) {
  return /\btracked\s*24\b/i.test(String(text || ''));
}

export function isTracked24ShippingMethod(method) {
  return /\btracked\s*24\b/i.test(String(method || ''));
}

const phraseIsPresent = (pageWords, phrase) =>
  phrase && (` ${pageWords} `).includes(` ${phrase} `);

const phraseTokenCoverage = (pageWordSet, phrase) => {
  const tokens = phrase.split(' ').filter((token) => token.length > 2);
  if (!tokens.length) return 0;
  return tokens.filter((token) => pageWordSet.has(token)).length / tokens.length;
};

/**
 * Avoid substring false positives (e.g. order 1234 matching text containing 12345).
 * Prefer space-separated page words (keeps 3656 / #11713 as separate tokens).
 * Digit-only ids must match an exact digit run; mixed tokens (postcodes) may match
 * the space-stripped word stream.
 * @param {{ words?: string, normalizedText?: string }} page
 * @param {string} identifier
 */
export function pageTextHasIdentifier(page, identifier) {
  const needle = String(identifier || '');
  if (!needle || needle.length < 3) return false;
  const words = ` ${String(page?.words || '').toLowerCase()} `;
  if (words.includes(` ${needle} `)) return true;
  const joinedFromWords = String(page?.words || '')
    .toLowerCase()
    .replace(/\s+/g, '');
  if (/[a-z]/.test(needle)) {
    return (
      joinedFromWords.includes(needle) ||
      String(page?.normalizedText || '').includes(needle)
    );
  }
  return (
    normalizedTextHasExactIdentifier(joinedFromWords, needle) ||
    normalizedTextHasExactIdentifier(page?.normalizedText, needle)
  );
}

/**
 * @param {string} normalizedHaystack
 * @param {string} identifier
 */
export function normalizedTextHasExactIdentifier(normalizedHaystack, identifier) {
  const hay = String(normalizedHaystack || '');
  const needle = String(identifier || '');
  if (!hay || !needle || needle.length < 3) return false;
  if (hay === needle) return true;
  const runs = hay.match(/[a-z]+|\d+/g) || [];
  return runs.includes(needle);
}

const scoreOrderAgainstPage = (profile, page, weekProximity = 'unknown', addressPage = null) => {
  let score = 0;
  const reasons = [];
  const add = (points, reason) => {
    score += points;
    reasons.push(reason);
  };
  const tracked24Page = isTracked24LabelPage(page?.text || page?.words || '');
  // Identifiers (order / source / customer ref) use the full page — they are not return-address noise.
  const matchedIdentifier = (profile.identifiers || []).find((identifier) =>
    pageTextHasIdentifier(page, identifier),
  );
  if (matchedIdentifier) {
    const pageBlob = `${page.text || ''} ${page.words || ''}`;
    const looksLikeCustomerRef =
      /customer\s*ref/i.test(pageBlob) ||
      (profile.identifiers.length > 1 &&
        matchedIdentifier !== profile.identifiers[0]);
    add(120, looksLikeCustomerRef ? 'customer ref' : 'order/source number');
  }

  // Address-like evidence ignores return / sender blocks (Spa Shop / market false matches).
  const geo = addressPage || page;
  if (
    (profile.postcodes || []).some(
      (postcode) =>
        !isLondonSmokeReturnAddressPhrase(postcode) &&
        pageTextHasIdentifier(geo, postcode),
    )
  ) {
    add(48, 'postcode');
  }
  if (
    profile.names.some(
      (name) =>
        !isLondonSmokeReturnAddressPhrase(name) && phraseIsPresent(geo.words, name),
    )
  ) {
    add(38, 'recipient name');
  } else if (
    profile.names.some(
      (name) =>
        !isLondonSmokeReturnAddressPhrase(name) &&
        phraseTokenCoverage(geo.wordSet, name) === 1,
    )
  ) {
    add(30, 'recipient name tokens');
  }

  // Tracked 24 faces lean on company names in the open week folder (no Customer Ref).
  const companyExact = (profile.companies || []).some(
    (company) =>
      !isLondonSmokeReturnAddressPhrase(company) &&
      phraseIsPresent(geo.words, company),
  );
  const companyTokens = (profile.companies || []).some(
    (company) =>
      !isLondonSmokeReturnAddressPhrase(company) &&
      phraseTokenCoverage(geo.wordSet, company) >= 0.8,
  );
  if (companyExact) {
    add(tracked24Page ? 58 : 32, tracked24Page ? 'company (Tracked 24)' : 'company');
  } else if (companyTokens) {
    add(
      tracked24Page ? 48 : 24,
      tracked24Page ? 'company tokens (Tracked 24)' : 'company tokens',
    );
  }

  if (
    profile.addressLines.some(
      (line) =>
        !isLondonSmokeReturnAddressPhrase(line) && phraseIsPresent(geo.words, line),
    )
  ) {
    add(34, 'address');
  } else if (
    profile.addressLines.some(
      (line) =>
        !isLondonSmokeReturnAddressPhrase(line) &&
        phraseTokenCoverage(geo.wordSet, line) >= 0.7,
    )
  ) {
    add(22, 'address tokens');
  }
  if (
    score > 0 &&
    profile.cities.some(
      (city) =>
        !isLondonSmokeReturnAddressPhrase(city) && phraseIsPresent(geo.words, city),
    )
  ) {
    add(8, 'town/city');
  }

  if (tracked24Page && profile.isTracked24Order && score > 0) {
    add(24, 'Tracked 24 method');
  }

  // Soft week preference only after some label evidence exists.
  // Tracked 24 company hits lean harder on the open week folder.
  if (score > 0) {
    let boost = WEEK_PROXIMITY_BOOST[weekProximity] || 0;
    if (
      tracked24Page &&
      weekProximity === 'active' &&
      (companyExact || companyTokens)
    ) {
      boost = Math.max(boost, 55);
    }
    if (boost > 0) {
      add(
        boost,
        weekProximity === 'active' ? 'working week folder' : 'adjacent week folder',
      );
    }
  }
  return { score, reasons, weekProximity, tracked24Page };
};

const buildPageTextViews = (page) => {
  const fullWords = page.words || normalizeWords(page.text);
  const full = {
    ...page,
    words: fullWords,
    wordSet: page.wordSet || new Set(fullWords.split(' ').filter(Boolean)),
    normalizedText: page.normalizedText || normalize(page.text),
  };
  const scrubbedText = scrubReturnAddressFromLabelText(page.text || '');
  const scrubbedWords = normalizeWords(scrubbedText);
  const addressPage = {
    text: scrubbedText,
    words: scrubbedWords,
    wordSet: new Set(scrubbedWords.split(' ').filter(Boolean)),
    normalizedText: normalize(scrubbedText),
  };
  return { full, addressPage };
};

const readBlobArrayBuffer = (blob) => {
  if (typeof blob?.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Unable to read PDF data.'));
    reader.readAsArrayBuffer(blob);
  });
};

export async function extractShippingLabelPdfPages(blob) {
  if (!(blob instanceof Blob) || !blob.size) throw new Error('A PDF file is required.');
  const bytes = new Uint8Array(await readBlobArrayBuffer(blob));
  const loadingTask = pdfjsLib.getDocument({ data: bytes });
  const document = await loadingTask.promise;
  const pages = [];
  try {
    for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
      const page = await document.getPage(pageNumber);
      const content = await page.getTextContent();
      const text = content.items.map((item) => item.str || '').join(' ').replace(/\s+/g, ' ').trim();
      const words = normalizeWords(text);
      pages.push({
        pageNumber,
        text,
        words,
        wordSet: new Set(words.split(' ').filter(Boolean)),
        normalizedText: normalize(text),
      });
      page.cleanup();
    }
  } finally {
    await document.destroy();
  }
  return pages;
}

/**
 * @param {object[]} pages
 * @param {object[]} orders
 * @param {{ workingDeliveryDate?: string }} [options]
 *   Soft preference: Printing Toolkit delivery dropdown week (OrderDesk folder
 *   week ending Friday), plus previous and next week. Still searches all
 *   candidates; staff can always reassign manually.
 */
export function matchShippingLabelPagesToOrders(pages, orders, options = {}) {
  const workingDeliveryDate = options.workingDeliveryDate || '';
  const proximityRank = { active: 0, adjacent: 1, unknown: 2, outside: 3 };
  const candidates = (orders || []).map((order) => ({
    order,
    orderKey: getQueueOrderKey(order),
    profile: orderMatchProfile(order),
    weekProximity: classifyOrderWeekProximity(order, workingDeliveryDate),
  }));
  return (pages || []).map((page) => {
    const { full: scoredPage, addressPage } = buildPageTextViews(page);
    const matches = candidates
      .map((candidate) => ({
        ...candidate,
        ...scoreOrderAgainstPage(
          candidate.profile,
          scoredPage,
          candidate.weekProximity,
          addressPage,
        ),
      }))
      .filter((candidate) => candidate.score >= 20)
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return (
          (proximityRank[left.weekProximity] ?? 9) -
          (proximityRank[right.weekProximity] ?? 9)
        );
      })
      .slice(0, 5);
    const top = matches[0];
    const runnerUp = matches[1];
    const topHasCompany =
      top &&
      (top.reasons || []).some((reason) => /company/i.test(String(reason || '')));
    const tracked24CompanyUnique =
      top?.tracked24Page &&
      topHasCompany &&
      top.weekProximity === 'active' &&
      top.score >= 30 &&
      (!runnerUp ||
        top.score - runnerUp.score >= 10 ||
        !(runnerUp.reasons || []).some((reason) => /company/i.test(String(reason || ''))));
    const confidentlyUnique =
      (top &&
        top.score >= 30 &&
        (!runnerUp || top.score - runnerUp.score >= 15)) ||
      tracked24CompanyUnique;
    return {
      pageNumber: page.pageNumber,
      textPreview: page.text.slice(0, 180),
      matches: matches.map((candidate) => ({
        orderKey: candidate.orderKey,
        order: candidate.order,
        score: candidate.score,
        reasons: candidate.reasons,
        weekProximity: candidate.weekProximity,
      })),
      /** Always the top scorer when any match exists — UI Align uses this. */
      bestOrderKey: top?.orderKey || '',
      suggestedOrderKey: confidentlyUnique ? top.orderKey : '',
      status: confidentlyUnique
        ? 'matched'
        : matches.length
          ? 'ambiguous'
          : 'unmatched',
      matchSummary: top
        ? `Best match: order ${top.orderKey} via ${top.reasons.join(', ')}`
        : 'No order number, customer ref, recipient name, company, postcode, or address match found.',
    };
  });
}

export async function splitShippingLabelPdf(blob, assignments) {
  const source = await PDFDocument.load(await readBlobArrayBuffer(blob), {
    ignoreEncryption: false,
    updateMetadata: false,
  });
  const grouped = (assignments || []).reduce((map, assignment) => {
    if (!assignment.orderKey) return map;
    if (!map.has(assignment.orderKey)) map.set(assignment.orderKey, []);
    map.get(assignment.orderKey).push(Number(assignment.pageNumber));
    return map;
  }, new Map());
  const results = [];
  for (const [orderKey, pageNumbers] of grouped) {
    const output = await PDFDocument.create();
    const indexes = Array.from(new Set(pageNumbers))
      .filter((page) => page >= 1 && page <= source.getPageCount())
      .sort((a, b) => a - b)
      .map((page) => page - 1);
    const copied = await output.copyPages(source, indexes);
    copied.forEach((page) => output.addPage(page));
    const bytes = await output.save({ useObjectStreams: true });
    results.push({
      orderKey,
      pageNumbers: indexes.map((index) => index + 1),
      pageCount: indexes.length,
      blob: new Blob([bytes], { type: 'application/pdf' }),
    });
  }
  return results;
}
